export type TreeLayoutRuleId =
  | 'TREE-RULE-GENERATION-ROW'
  | 'TREE-RULE-CHILD-BELOW-PARENT'
  | 'TREE-RULE-CHILD-WITHIN-PARENT-CLUSTER'
  | 'TREE-RULE-SIBLING-CLUSTER-CONTIGUOUS'
  | 'TREE-RULE-BRANCH-NONINTERLEAVING'
  | 'TREE-RULE-COPARENT-SHARED-CHILD'
  | 'TREE-RULE-SPOUSE-PARTNER-LATERAL'
  | 'TREE-RULE-VIEWER-CENTER'
  | 'TREE-RULE-CARD-NONOVERLAP';

export interface TreeLayoutAuthorityCard {
  memberId: string;
  x: number;
  y: number;
  tier: number;
  parentIds: string[];
  branchPath: string | null;
  spouseId: string | null;
  isYou?: boolean;
}

export interface TreeLayoutAuthorityViolation {
  rule: TreeLayoutRuleId;
  memberIds: string[];
  details: string;
}

export interface TreeLayoutAuthorityReport {
  status: 'PASS' | 'FAIL';
  violations: TreeLayoutAuthorityViolation[];
  checkedMemberCount: number;
  checkedParentChildCount: number;
  checkedSiblingClusterCount: number;
  checkedBranchCount: number;
}

export interface TreeLayoutAuthorityOptions {
  rowGap: number;
  cardWidth: number;
  tolerance?: number;
}

interface Interval {
  min: number;
  max: number;
}

const intervalFor = (
  cards: TreeLayoutAuthorityCard[],
  cardWidth: number,
): Interval | null => {
  if (cards.length === 0) return null;

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const card of cards) {
    min = Math.min(min, card.x - cardWidth / 2);
    max = Math.max(max, card.x + cardWidth / 2);
  }

  return { min, max };
};

const intervalsOverlap = (
  left: Interval,
  right: Interval,
  tolerance: number,
): boolean =>
  left.min < right.max - tolerance &&
  left.max > right.min + tolerance;

const parentClusterCenter = (
  parents: TreeLayoutAuthorityCard[],
): number =>
  parents.reduce((sum, parent) => sum + parent.x, 0) /
  Math.max(parents.length, 1);

export function enforceTreeLayoutRules(
  cards: TreeLayoutAuthorityCard[],
  options: TreeLayoutAuthorityOptions,
): TreeLayoutAuthorityReport {
  const tolerance = options.tolerance ?? 0.001;
  const violations: TreeLayoutAuthorityViolation[] = [];
  const byId = new Map(cards.map(card => [card.memberId, card]));
  let checkedParentChildCount = 0;

  for (const card of cards) {
    const expectedY = -card.tier * options.rowGap;
    if (Math.abs(card.y - expectedY) > tolerance) {
      violations.push({
        rule: 'TREE-RULE-GENERATION-ROW',
        memberIds: [card.memberId],
        details: `Member ${card.memberId} is at y=${card.y}; expected ${expectedY}.`,
      });
    }

    const parents = card.parentIds
      .map(parentId => byId.get(parentId))
      .filter((parent): parent is TreeLayoutAuthorityCard => Boolean(parent));

    if (parents.length === 0) continue;
    checkedParentChildCount += 1;

    const wrongVerticalOrder = parents.some(
      parent => parent.tier !== card.tier + 1 || parent.y >= card.y - tolerance,
    );

    if (wrongVerticalOrder) {
      violations.push({
        rule: 'TREE-RULE-CHILD-BELOW-PARENT',
        memberIds: [...parents.map(parent => parent.memberId), card.memberId],
        details: `Child ${card.memberId} is not one governed generation row below every visible parent.`,
      });
    }

    const clusterCenter = parentClusterCenter(parents);
    const allowedOffset =
      Math.max(options.cardWidth, parents.length * options.cardWidth) +
      options.cardWidth / 2;

    if (Math.abs(card.x - clusterCenter) > allowedOffset + tolerance) {
      violations.push({
        rule: 'TREE-RULE-CHILD-WITHIN-PARENT-CLUSTER',
        memberIds: [...parents.map(parent => parent.memberId), card.memberId],
        details: `Child ${card.memberId} is outside the horizontal cluster owned by its visible parent set.`,
      });
    }

    if (parents.length > 1) {
      const parentInterval = intervalFor(parents, options.cardWidth);
      if (
        parentInterval &&
        (card.x < parentInterval.min - options.cardWidth - tolerance ||
          card.x > parentInterval.max + options.cardWidth + tolerance)
      ) {
        violations.push({
          rule: 'TREE-RULE-COPARENT-SHARED-CHILD',
          memberIds: [...parents.map(parent => parent.memberId), card.memberId],
          details: `Shared child ${card.memberId} is not governed beneath its visible co-parent interval.`,
        });
      }
    }
  }

  const siblingGroups = new Map<string, TreeLayoutAuthorityCard[]>();
  for (const card of cards) {
    const visibleParentIds = card.parentIds
      .filter(parentId => byId.has(parentId))
      .slice()
      .sort();
    if (visibleParentIds.length === 0) continue;
    const key = visibleParentIds.join('|');
    const group = siblingGroups.get(key) ?? [];
    group.push(card);
    siblingGroups.set(key, group);
  }

  for (const [parentKey, siblings] of siblingGroups) {
    if (siblings.length < 2) continue;
    const ordered = siblings.slice().sort((a, b) => a.x - b.x);
    const siblingIds = new Set(ordered.map(card => card.memberId));
    const minX = ordered[0].x;
    const maxX = ordered[ordered.length - 1].x;
    const interleaving = cards.filter(
      card =>
        !siblingIds.has(card.memberId) &&
        card.tier === ordered[0].tier &&
        card.x > minX + tolerance &&
        card.x < maxX - tolerance,
    );

    if (interleaving.length > 0) {
      violations.push({
        rule: 'TREE-RULE-SIBLING-CLUSTER-CONTIGUOUS',
        memberIds: [
          ...ordered.map(card => card.memberId),
          ...interleaving.map(card => card.memberId),
        ],
        details: `Sibling cluster for visible parent set ${parentKey} is interleaved by unrelated same-tier cards.`,
      });
    }
  }

  const branchGroups = new Map<string, TreeLayoutAuthorityCard[]>();
  for (const card of cards) {
    if (!card.branchPath || card.branchPath === 'self') continue;
    const root = card.branchPath.split('.').slice(0, 2).join('.');
    const group = branchGroups.get(root) ?? [];
    group.push(card);
    branchGroups.set(root, group);
  }

  const branchIntervals = Array.from(branchGroups.entries())
    .map(([branch, branchCards]) => ({
      branch,
      cards: branchCards,
      interval: intervalFor(branchCards, options.cardWidth),
    }))
    .filter(
      (entry): entry is {
        branch: string;
        cards: TreeLayoutAuthorityCard[];
        interval: Interval;
      } => Boolean(entry.interval),
    );

  for (let leftIndex = 0; leftIndex < branchIntervals.length; leftIndex += 1) {
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < branchIntervals.length;
      rightIndex += 1
    ) {
      const left = branchIntervals[leftIndex];
      const right = branchIntervals[rightIndex];
      if (!intervalsOverlap(left.interval, right.interval, tolerance)) continue;

      violations.push({
        rule: 'TREE-RULE-BRANCH-NONINTERLEAVING',
        memberIds: [
          ...left.cards.map(card => card.memberId),
          ...right.cards.map(card => card.memberId),
        ],
        details: `Branch intervals ${left.branch} and ${right.branch} overlap instead of remaining contiguous and isolated.`,
      });
    }
  }

  for (const card of cards) {
    if (!card.spouseId) continue;
    const spouse = byId.get(card.spouseId);
    if (!spouse || card.memberId > spouse.memberId) continue;

    if (
      card.tier !== spouse.tier ||
      Math.abs(card.y - spouse.y) > tolerance
    ) {
      violations.push({
        rule: 'TREE-RULE-SPOUSE-PARTNER-LATERAL',
        memberIds: [card.memberId, spouse.memberId],
        details: `Spouse or partner pair ${card.memberId}/${spouse.memberId} is not lateral on one generation row.`,
      });
    }
  }

  const viewer = cards.find(card => card.isYou);
  if (
    viewer &&
    (Math.abs(viewer.x) > tolerance || Math.abs(viewer.y) > tolerance)
  ) {
    violations.push({
      rule: 'TREE-RULE-VIEWER-CENTER',
      memberIds: [viewer.memberId],
      details: `Viewer ${viewer.memberId} is not protected at the layout origin.`,
    });
  }

  for (let leftIndex = 0; leftIndex < cards.length; leftIndex += 1) {
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < cards.length;
      rightIndex += 1
    ) {
      const left = cards[leftIndex];
      const right = cards[rightIndex];
      if (Math.abs(left.y - right.y) > tolerance) continue;
      if (
        Math.abs(left.x - right.x) <
        options.cardWidth - tolerance
      ) {
        violations.push({
          rule: 'TREE-RULE-CARD-NONOVERLAP',
          memberIds: [left.memberId, right.memberId],
          details: `Same-row cards ${left.memberId} and ${right.memberId} overlap.`,
        });
      }
    }
  }

  return {
    status: violations.length > 0 ? 'FAIL' : 'PASS',
    violations,
    checkedMemberCount: cards.length,
    checkedParentChildCount,
    checkedSiblingClusterCount: Array.from(siblingGroups.values()).filter(
      siblings => siblings.length > 1,
    ).length,
    checkedBranchCount: branchGroups.size,
  };
}
